import { createContext, useContext } from 'react';
import { useCallback, useState } from 'react';

import { AppMessage } from '@autopass-web/mochi-component';

type ToastContextProps = {
  onToastSuccess: (message: string) => void;
  onToastError: (message: string) => void;
};

const ToastContext = createContext<ToastContextProps | null>(null);

export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'failure';
  } | null>(null);

  const handleToastSuccess = useCallback((message: string) => {
    setToast({ message, type: 'success' });
  }, []);

  const handleToastError = useCallback((message: string) => {
    setToast({ message, type: 'failure' });
  }, []);

  const handleToastClose = useCallback(() => {
    setToast(null);
  }, []);

  return (
    <ToastContext.Provider
      value={{
        onToastError: handleToastError,
        onToastSuccess: handleToastSuccess,
      }}
    >
      <AppMessage
        show={toast != null}
        message={toast?.message ?? ''}
        onDismiss={handleToastClose}
        type={toast?.type}
        autoDismiss
      />
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('toast must be used within an ToastProvider');
  }

  return context;
};
